import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ADULT_BASE_PRICE, KIDS_BASE_PRICE, PRODUCT_TYPES } from "../data/catalog";
import { getAllCategories } from "../lib/dynamicCatalog";
import { BoltIcon } from "../components/Bolts";
import { Input } from "../components/ui/input";
import { ArrowLeft, Save, Tag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CategoryPricingPage = () => {
    const [defaults, setDefaults] = useState({});
    const [drafts, setDrafts] = useState({});
    const [saving, setSaving] = useState({});

    const rows = useMemo(() => {
        const out = [];
        for (const cat of getAllCategories()) {
            if (cat.sub && cat.sub.length > 0) {
                for (const s of cat.sub) {
                    out.push({
                        key: `${cat.slug}/${s.slug}`,
                        label: `${cat.name} · ${s.name}`,
                        catSlug: cat.slug,
                        productType: s.productType,
                    });
                }
            } else {
                out.push({
                    key: cat.slug,
                    label: cat.name,
                    catSlug: cat.slug,
                    productType: cat.productType,
                });
            }
        }
        return out;
    }, []);

    const load = async () => {
        try {
            const { data } = await axios.get(`${API}/category-defaults`);
            const map = {};
            (data || []).forEach((d) => (map[d.key] = d.price_cents));
            setDefaults(map);
            const dMap = {};
            Object.entries(map).forEach(([k, v]) => (dMap[k] = (v / 100).toFixed(2)));
            setDrafts(dMap);
        } catch {}
    };

    useEffect(() => {
        load();
    }, []);

    const save = async (key) => {
        const raw = (drafts[key] || "").trim();
        const cents = raw ? Math.round(parseFloat(raw) * 100) : 0;
        if (isNaN(cents) || cents < 0) {
            toast.error("Enter a valid price (e.g. 17.00)");
            return;
        }
        setSaving((s) => ({ ...s, [key]: true }));
        try {
            await axios.put(`${API}/category-defaults/${key}`, { price_cents: cents });
            setDefaults((d) => ({ ...d, [key]: cents }));
            toast.success("Saved.");
        } catch {
            toast.error("Save failed.");
        } finally {
            setSaving((s) => ({ ...s, [key]: false }));
        }
    };

    return (
        <div data-testid="category-pricing-page" className="min-h-[80vh]">
            <section className="border-b border-white/10 bg-storm-navy">
                <div className="container-x py-14 md:py-20">
                    <Link
                        to="/admin"
                        data-testid="back-to-admin"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-storm-bolt mb-5 hover:gap-3 transition-all"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to Admin
                    </Link>
                    <p className="eyebrow flex items-center gap-2 mb-4">
                        <BoltIcon className="w-3.5 h-3.5" /> Pricing
                    </p>
                    <h1 className="text-4xl md:text-6xl text-white leading-[0.9]">
                        Category <span className="text-storm-bolt">defaults.</span>
                    </h1>
                    <p className="text-storm-mist mt-4 max-w-2xl">
                        Set one price per sub-category. Every listing in that sub-category uses this
                        price unless you override it on an individual photo.
                    </p>
                </div>
            </section>

            <section className="container-x py-12 md:py-16 space-y-3">
                <div className="surface-solid p-5 md:p-6 border-l-4 border-l-storm-bolt">
                    <p className="font-heading text-lg uppercase tracking-wider text-white mb-1">
                        T-Shirts — hard-coded
                    </p>
                    <p className="text-sm text-storm-mist">
                        Adult <span className="text-white">${ADULT_BASE_PRICE}</span> base · Kids{" "}
                        <span className="text-white">${KIDS_BASE_PRICE}</span> flat · 2X +$2 · 3X +$4 · 4X +$6.
                        These are fixed in code — every shirt listing uses them automatically.
                    </p>
                </div>

                {rows.map((r) => {
                    if (r.productType === PRODUCT_TYPES.SHIRT) return null;
                    const current = defaults[r.key];
                    return (
                        <div
                            key={r.key}
                            data-testid={`pricing-row-${r.key.replace("/", "-")}`}
                            className="surface-solid p-4 md:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                        >
                            <div className="flex items-center gap-3 flex-1">
                                <Tag className="w-4 h-4 text-storm-bolt flex-shrink-0" />
                                <div>
                                    <div className="font-heading text-sm md:text-base uppercase tracking-wider text-white">
                                        {r.label}
                                    </div>
                                    {current != null && (
                                        <div className="text-xs text-storm-mist mt-0.5">
                                            Current: ${(current / 100).toFixed(2)}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                                <div className="relative flex-1 sm:flex-initial sm:w-36">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-storm-mist text-sm">$</span>
                                    <Input
                                        type="text"
                                        inputMode="decimal"
                                        value={drafts[r.key] ?? ""}
                                        onChange={(e) =>
                                            setDrafts((d) => ({
                                                ...d,
                                                [r.key]: e.target.value.replace(/[^0-9.]/g, ""),
                                            }))
                                        }
                                        placeholder="0.00"
                                        data-testid={`pricing-input-${r.key.replace("/", "-")}`}
                                        className="bg-storm-void border-white/10 text-white placeholder:text-storm-mist/50 rounded-none h-11 pl-7 focus-visible:ring-storm-bolt focus-visible:ring-offset-0"
                                    />
                                </div>
                                <button
                                    onClick={() => save(r.key)}
                                    disabled={!!saving[r.key]}
                                    data-testid={`pricing-save-${r.key.replace("/", "-")}`}
                                    className="inline-flex items-center justify-center gap-1.5 bg-storm-bolt text-black px-4 h-11 font-heading text-sm uppercase tracking-wider hover:bg-white disabled:opacity-50"
                                >
                                    <Save className="w-4 h-4" /> {saving[r.key] ? "…" : "Save"}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </section>
        </div>
    );
};

export default CategoryPricingPage;
